import React, { useState, useMemo } from 'react';
import { TrendingUp, ChevronDown, Globe, Monitor, Smartphone, Tablet } from 'lucide-react';

/**
 * EngagementTimeline - Activity with progressive disclosure
 * Refined: Clean, professional look
 */
const EngagementTimeline = ({ dailyActivity = [], geographic = [], device = [] }) => {
    const [showAudience, setShowAudience] = useState(false);

    const stats = useMemo(() => {
        if (!dailyActivity || dailyActivity.length === 0) {
            return { today: 0, avg: 0, trend: 0, total: 0 };
        }
        const total = dailyActivity.reduce((sum, d) => sum + d.count, 0);
        const avg = Math.round(total / dailyActivity.length);
        const today = dailyActivity[dailyActivity.length - 1]?.count || 0;
        const yesterday = dailyActivity[dailyActivity.length - 2]?.count || avg;
        const trend = yesterday > 0 ? Math.round(((today - yesterday) / yesterday) * 100) : 0;
        return { today, avg, trend, total };
    }, [dailyActivity]);

    const chartPath = useMemo(() => {
        if (!dailyActivity || dailyActivity.length < 2) return null;
        const width = 100, height = 50;
        const padding = { top: 5, right: 2, bottom: 5, left: 2 };
        const chartWidth = width - padding.left - padding.right;
        const chartHeight = height - padding.top - padding.bottom;
        const max = Math.max(...dailyActivity.map(d => d.count)) || 1;

        const points = dailyActivity.map((item, i) => {
            const x = padding.left + (i / (dailyActivity.length - 1)) * chartWidth;
            const y = padding.top + chartHeight - (item.count / max) * chartHeight;
            return { x, y };
        });

        const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
        const areaPath = `${linePath} L ${points[points.length - 1].x} ${height - padding.bottom} L ${padding.left} ${height - padding.bottom} Z`;
        return { linePath, areaPath };
    }, [dailyActivity]);

    const topCountries = geographic.slice(0, 3);
    const deviceBreakdown = useMemo(() => {
        const breakdown = device.reduce((acc, d) => {
            const type = (d.deviceType || 'unknown').toLowerCase();
            acc[type] = (acc[type] || 0) + d.count;
            return acc;
        }, {});
        const total = Object.values(breakdown).reduce((a, b) => a + b, 0);
        return { breakdown, total };
    }, [device]);

    const getCountryFlag = (code) => {
        if (!code || code.length !== 2) return '🌍';
        return String.fromCodePoint(...code.toUpperCase().split('').map(c => 127397 + c.charCodeAt(0)));
    };

    if (!dailyActivity || dailyActivity.length === 0) {
        return (
            <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
                <div className="flex items-center gap-1.5 mb-4">
                    <TrendingUp size={14} className="text-slate-400" />
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Engagement Timeline</h3>
                </div>
                <div className="text-center py-8">
                    <p className="text-sm text-slate-400">No activity data yet</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden">
            {/* Header */}
            <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-1.5">
                        <TrendingUp size={14} className="text-slate-400" />
                        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Engagement Timeline</h3>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                        <div className="text-right">
                            <span className="text-slate-400 block text-[10px]">Today</span>
                            <span className="font-semibold text-slate-700">{stats.today}</span>
                        </div>
                        <div className="text-right">
                            <span className="text-slate-400 block text-[10px]">Daily Avg</span>
                            <span className="font-semibold text-slate-700">{stats.avg}</span>
                        </div>
                        {stats.trend !== 0 && (
                            <span className={`text-[10px] font-medium ${stats.trend > 0 ? 'text-emerald-600' : 'text-slate-500'}`}>
                                {stats.trend > 0 ? '+' : ''}{stats.trend}%
                            </span>
                        )}
                    </div>
                </div>

                {/* Chart */}
                {chartPath && (
                    <div className="h-20">
                        <svg className="w-full h-full" viewBox="0 0 100 50" preserveAspectRatio="none">
                            <defs>
                                <linearGradient id="timelineGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.15" />
                                    <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
                                </linearGradient>
                            </defs>
                            <path d={chartPath.areaPath} fill="url(#timelineGradient)" />
                            <path
                                d={chartPath.linePath} fill="none" stroke="#8b5cf6" strokeWidth="1.5"
                                strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke"
                            />
                        </svg>
                    </div>
                )}

                <div className="flex justify-between mt-1">
                    {dailyActivity[0] && (
                        <span className="text-[10px] text-slate-400">
                            {new Date(dailyActivity[0].day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                    )}
                    {dailyActivity[dailyActivity.length - 1] && (
                        <span className="text-[10px] text-slate-400">
                            {new Date(dailyActivity[dailyActivity.length - 1].day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                    )}
                </div>
            </div>

            {/* Collapsible Audience */}
            <div className="border-t border-slate-100">
                <button
                    onClick={() => setShowAudience(!showAudience)}
                    className="w-full px-5 py-3 flex items-center justify-between text-xs font-medium text-slate-500 hover:bg-slate-50 transition-colors"
                >
                    <span>Audience Details</span>
                    <ChevronDown size={14} className={`transition-transform ${showAudience ? 'rotate-180' : ''}`} />
                </button>

                {showAudience && (
                    <div className="px-5 pb-5 grid grid-cols-2 gap-4">
                        <div>
                            <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                                <Globe size={10} /> Countries
                            </div>
                            <div className="space-y-1">
                                {topCountries.map((c, i) => (
                                    <div key={i} className="flex items-center justify-between text-xs">
                                        <span className="flex items-center gap-1.5">
                                            <span>{getCountryFlag(c.country)}</span>
                                            <span className="text-slate-600">{c.country}</span>
                                        </span>
                                        <span className="text-slate-400">{c.count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                                <Monitor size={10} /> Devices
                            </div>
                            <div className="flex gap-3">
                                {[{ key: 'desktop', Icon: Monitor }, { key: 'mobile', Icon: Smartphone }, { key: 'tablet', Icon: Tablet }].map(({ key, Icon }) => {
                                    const count = deviceBreakdown.breakdown[key] || 0;
                                    const percent = deviceBreakdown.total > 0 ? Math.round((count / deviceBreakdown.total) * 100) : 0;
                                    if (percent === 0) return null;
                                    return (
                                        <div key={key} className="text-center">
                                            <Icon size={12} className="text-slate-400 mx-auto mb-0.5" />
                                            <div className="text-xs font-semibold text-slate-700">{percent}%</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EngagementTimeline;
